import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { AMAZON_WWDSS, scrollToId } from "@/lib/site";

const NAV = [
  { label: "Books", id: "books" },
  { label: "About Grace", id: "about" },
  { label: "Trailer", id: "trailer" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 70);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled
            ? "bg-ink/90 backdrop-blur-md border-b border-bone/10 py-4"
            : "bg-transparent border-b border-transparent py-6"
        }`}
      >
        <div className="mx-auto max-w-[1500px] px-6 sm:px-10 flex items-center justify-between">
          <button
            onClick={() => go("hero")}
            data-testid="brand-logo"
            className="text-left leading-none focus:outline-none"
          >
            <span className="block font-serif text-lg sm:text-xl tracking-[0.2em] uppercase text-bone">
              Ride Like a Lady
            </span>
            <span className="mt-0.5 block font-sans text-[0.55rem] tracking-[0.42em] uppercase text-bone-muted">
              Grace Edwards
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-10">
            {NAV.map((n) => (
              <button
                key={n.id}
                onClick={() => go(n.id)}
                data-testid={`nav-${n.id}`}
                className="font-sans text-[0.7rem] uppercase tracking-[0.28em] text-bone-soft hover:text-bone transition-colors duration-300"
              >
                {n.label}
              </button>
            ))}
            <a
              href={AMAZON_WWDSS}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-buy-the-book"
              className="font-sans text-[0.7rem] uppercase tracking-[0.28em] text-ink bg-bone px-5 py-2.5 hover:bg-rust hover:text-bone transition-colors duration-400 border border-bone"
            >
              Buy the Book
            </a>
          </nav>

          <button
            className="md:hidden text-bone p-2 -mr-2"
            onClick={() => setOpen(true)}
            data-testid="nav-menu-open"
            aria-label="Open menu"
          >
            <Menu strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] bg-ink-pure md:hidden flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 py-6 border-b border-bone/10">
              <span className="font-serif text-lg tracking-[0.2em] uppercase text-bone">
                Ride Like a Lady
              </span>
              <button
                onClick={() => setOpen(false)}
                data-testid="nav-menu-close"
                aria-label="Close menu"
                className="text-bone p-2 -mr-2"
              >
                <X strokeWidth={1.5} />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center px-8 gap-8">
              {NAV.map((n, i) => (
                <motion.button
                  key={n.id}
                  onClick={() => go(n.id)}
                  data-testid={`mobile-nav-${n.id}`}
                  className="text-left font-serif text-5xl tracking-tight text-bone"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                >
                  {n.label}
                </motion.button>
              ))}
              <a
                href={AMAZON_WWDSS}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-buy"
                className="mt-6 inline-flex items-center justify-center bg-bone text-ink font-sans text-sm uppercase tracking-[0.28em] py-4 border border-bone"
              >
                Buy the Book
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
